import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { Option } from "components/Form/form";
import {
    setForexProducts,
    setForextLeadType,
} from "redux/features/CreateOpportunity";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { AxiosRequestConfig } from "axios";
import { getCurrency, GetCurrencyRequestType } from "service/apis/CreateForexQuotation";
import { toast, ToastContainer } from "react-toastify";

const productOptions: Option[] = [
    { value: "sale", label: "Sale" },
    { value: "encashment", label: "Encashment" },
];

interface Step1Props {
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
    setContactDetails?: React.Dispatch<React.SetStateAction<any>>;
    setPageTitle: React.Dispatch<React.SetStateAction<string>>;
    setProcessState: React.Dispatch<React.SetStateAction<any>>;
}

export const ForexSelectType: React.FC<Step1Props> = ({
    setCurrentStep,
    setProcessState,
}) => {
    const { forexLeadType } = useAppSelector(
        (state) => state.CreateOpportunity,
    );
    const dispatch = useAppDispatch();

    return (
        <div className="flex flex-col gap-6 max-w-md h-full">
            <form className="max-w-md h-full">
                <div className="flex flex-col sm:pl-0 pl-10 gap-8 w-full h-full ">
                    <div className="flex relative flex-col gap-3 items-start justify-start w-full">
                        <Tabs
                            className="flex rounded-lg gap-2"
                            value={forexLeadType || undefined}
                            onValueChange={val => dispatch(setForextLeadType(val as 'sale' | 'encashment'))}
                        >
                            <TabsList className=" max-h-[40px] h-full flex justify-start items-center gap-3 sm:flex-wrap">
                                {productOptions?.map(
                                    (tab: { label: string; value: string }) => (
                                        <TabsTrigger
                                            key={tab?.value}
                                            value={tab?.value}
                                            className={` py-2.5 px-[68px] text-sm h-[40px] font-normal rounded-lg ${tab.value === forexLeadType ? " bg-navbar-menu-bg text-blue-700" : " text-black-label bg-[#F1F1F1]"}`}
                                        >
                                            {tab?.label}
                                        </TabsTrigger>
                                    ),
                                )}
                            </TabsList>
                        </Tabs>
                    </div>
                    <div className="flex flex-row gap-8">
                        <button
                            className="w-[100px] sm:w-full text-[14px] text-blue-500 p-3 bg-transparent border border-blue-500 rounded-lg flex items-center justify-center gap-2"
                            onClick={() => {
                                setCurrentStep((prev) => prev - 1),
                                    setProcessState((prevState: any) => ({
                                        ...prevState,
                                        Product: "",
                                        "Product Group": "",
                                    }));
                            }}
                        >
                            <ChevronLeft color='blue' />
                            Back
                        </button>
                        <button
                            type="button"
                            onClick={()=>setCurrentStep((prev) => prev + 1)}
                            className="w-[100px] sm:w-full text-[14px] text-white-A700 p-3 bg-blue-700 border border-blue-500 rounded-lg flex items-center justify-center gap-2"                            
                        >                            
                            Next
                            <ChevronRight color='white' />
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

// Fetch currencies using shared service
const fetchCurrencies = async (): Promise<Currency[]> => {
    try {
        const payload: AxiosRequestConfig<GetCurrencyRequestType> = {};
        const { data } = await getCurrency(payload);

        const uniqueValues = data.filter((v, i, a) => a.findIndex(t => (t.currency === v.currency)) === i);
        return uniqueValues.map((item) => ({
            code: item.currency,
            name: item.country || item.currency,
        }));
    } catch (error) {
        console.error("Failed to fetch currencies", error);
        return [];
    }
};

// Types for data structure
export interface Currency {
    code: string;
    name: string;
}

export interface CurrencyAmount {
    id: string;
    currency: string;
    amount: string;
}

export interface ForexProduct {
    id: string;
    name: string;
    checked: boolean;
    currencies: CurrencyAmount[];
}

interface ForexProductSelectionProps extends Step1Props {}

export const ForexProductSelection: React.FC<ForexProductSelectionProps> = ({
    setCurrentStep,
}) => {
    const { forexProducts:products } = useAppSelector(
        (state) => state.CreateOpportunity,
    );
    const dispatch = useAppDispatch();    

    const [currencies, setCurrencies] = useState<Currency[]>([]);

    const getDefaultCurrencyCode = React.useCallback(() => {
        const usdCurrency = currencies.find((curr) => curr.code === "USD");
        return usdCurrency?.code || currencies[0]?.code || "USD";
    }, [currencies]);

    // Fetch currencies on mount
    React.useEffect(() => {
        const loadCurrencies = async () => {
            const data = await fetchCurrencies();
            setCurrencies(data);
        };
        loadCurrencies();
    }, []);

    // Toggle product selection
    const handleProductToggle = (productId: string) => {
        const defaultCurrencyCode = getDefaultCurrencyCode();
        dispatch(setForexProducts(
            products.map((product) => {
                if (product.id === productId) {
                    const newChecked = !product.checked;
                    return {
                        ...product,
                        checked: newChecked,
                        // Add first currency entry when checked, reset when unchecked
                        currencies: newChecked 
                            ? (product.currencies.length > 0 
                                ? product.currencies 
                                : [{
                                    id: `${productId}-currency-${Date.now()}`,
                                    currency: defaultCurrencyCode,
                                    amount: "",
                                }])
                            : [],
                    };
                }
                return product;
            }),
        ))        
    };

    // Add currency amount entry to a product
    const handleAddCurrency = (productId: string) => {
        const defaultCurrencyCode = getDefaultCurrencyCode();
        dispatch(setForexProducts(
            products.map((product) => {
                if (product.id === productId) {
                    return {
                        ...product,
                        currencies: [
                            ...product.currencies,
                            {
                                id: `${productId}-currency-${Date.now()}`,
                                currency: defaultCurrencyCode,
                                amount: "",
                            },
                        ],
                    };
                }
                return product;
            }),
        ))
    };

    // Update currency selection
    const handleCurrencyChange = (
        productId: string,
        currencyId: string,
        newCurrency: string
    ) => {
        dispatch(setForexProducts(
            products.map((product) => {
                if (product.id === productId) {
                    return {
                        ...product,
                        currencies: product.currencies.map((curr) =>
                            curr.id === currencyId
                                ? { ...curr, currency: newCurrency }
                                : curr
                        ),
                    };
                }
                return product;
            }),
        ))
    };


    // Update amount
    const handleAmountChange = (
        productId: string,
        currencyId: string,
        newAmount: string
    ) => {
        dispatch(setForexProducts(
            products.map((product) => {
                if (product.id === productId) {
                    return {
                        ...product,
                        currencies: product.currencies.map((curr) =>
                            curr.id === currencyId
                                ? { ...curr, amount: newAmount }
                                : curr
                        ),
                    };
                }
                return product;
            })
        ));
    };

    // Remove currency entry
    const handleRemoveCurrency = (productId: string, currencyId: string) => {
        dispatch(setForexProducts(
            products.map((product) => {
                if (product.id === productId) {
                    return {
                        ...product,
                        currencies: product.currencies.filter(
                            (curr) => curr.id !== currencyId
                        ),
                    };
                }
                return product;
            }),
        ));
    };

    // Validate and proceed
    const handleNext = () => {
        const selectedProducts = products.filter((p) => p.checked);

        if (selectedProducts.length === 0) {
            toast.error("Please select at least one product to proceed.");
            return;
        }

        // ✅ Validate duplicate currencies
        // ✅ Validate duplicate currencies across all selected products
        const duplicateProducts: string[] = [];

        for (const product of selectedProducts) {
            const currencyCodes = product.currencies.map((c) => c.currency);
            const uniqueCodes = new Set(currencyCodes);

            if (uniqueCodes.size !== currencyCodes.length) {
                duplicateProducts.push(product.name);
            }
        }

        if (duplicateProducts.length > 0) {
            const productList = duplicateProducts.join(", ");
            toast.error(`Can't  add same currency twice in: ${productList}`);
            return;
        }


        // ✅ Validate missing currency or amount
        const hasInvalidProduct = selectedProducts.some(
            (product) =>
                product.currencies.length === 0 ||
                product.currencies.some(
                    (curr) =>
                        !curr.currency ||
                        !curr.amount ||
                        parseFloat(curr.amount) <= 0
                )
        );

        if (hasInvalidProduct) {
            toast.error(
                "Please select currency and enter amount for all selected products."
            );
            return;
        }

        // ✅ If all good, proceed
        setCurrentStep(5);
    };


    const isNextDisabled = !products.some((p) => p.checked);

    return (
        <div className="flex flex-col gap-6 max-w-md h-full">
            <form className="max-w-md h-full" onSubmit={(e) => e.preventDefault()}>
                <div className="flex flex-col sm:pl-0 pl-10 gap-8 w-full h-full">
                    <div className="flex relative flex-col gap-3 items-start justify-start w-full">
                        <label className="text-[18px] font-medium text-[#333]">
                            Select at least any one to proceed.
                        </label>

                        <div className="flex w-full flex-col gap-4">
                            {products.map((product) => (
                                <div key={product.id} className="flex flex-col w-full">
                                    {/* Product Checkbox */}
                                    <label
                                        htmlFor={product.id}
                                        className={`${
                                            product.checked
                                                ? "border-blue-700"
                                                : "border-[#D2D2D2]"
                                        } flex w-full items-center rounded-lg border-2 gap-2.5 py-[14px] px-[10px] cursor-pointer`}
                                    >
                                        <Checkbox
                                            id={product.id}
                                            checked={product.checked}
                                            onCheckedChange={() =>
                                                handleProductToggle(product.id)
                                            }
                                            className="w-[19px] h-[19px] rounded-[4px] border border-[#D2D2D2]"
                                        />
                                        <p
                                            className={`text-[16px] font-medium w-full ${product.checked ? "text-[#165FE3]" : "text-[#333333]"}`}
                                        >
                                            {product.name}
                                        </p>
                                    </label>

                                    {/* Currency Amount Inputs */}
                                    {product.checked && (
                                        <div className="flex flex-col gap-4 rounded-lg bg-[#F9F9F9] p-4">
                                            {product.currencies.map((currencyAmount) => (
                                                <div key={currencyAmount.id} className="flex flex-col gap-2">
                                                    <div className="relative w-full">
                                                        <Select
                                                            value={currencyAmount.currency || undefined}
                                                            onValueChange={(value) =>
                                                                handleCurrencyChange(
                                                                    product.id,
                                                                    currencyAmount.id,
                                                                    value
                                                                )
                                                            }
                                                        >
                                                            <SelectTrigger className="absolute left-3 top-1 z-10 flex h-10 w-[72px] items-center justify-start rounded-lg border-none bg-[#E1EBFC] px-3 text-sm font-medium text-[#165FE3] shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0">
                                                                <SelectValue placeholder="Currency" />
                                                            </SelectTrigger>
                                                            <SelectContent className="max-h-60">
                                                                {currencies.map((curr) => (
                                                                    <SelectItem
                                                                        key={curr.code}
                                                                        value={curr.code}
                                                                    >
                                                                        {curr.code}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>

                                                        <Input
                                                            type="number"
                                                            placeholder="Enter amount"
                                                            value={currencyAmount.amount}
                                                            onChange={(e) =>
                                                                handleAmountChange(
                                                                    product.id,
                                                                    currencyAmount.id,
                                                                    e.target.value
                                                                )
                                                            }
                                                            className="h-12 w-full rounded-lg border border-gray-border-gray-lite pl-[90px] pr-3 text-sm placeholder:text-[#9CA3AF] focus-visible:ring-0 focus-visible:ring-offset-0"
                                                        />
                                                    </div>

                                                    {product.currencies.length > 1 && (
                                                        <div className="flex justify-end">
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleRemoveCurrency(
                                                                        product.id,
                                                                        currencyAmount.id
                                                                    )
                                                                }
                                                                className="text-sm font-medium text-red-500 hover:text-red-600"
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                            {/* Add Currency Button */}
                                            <button
                                                type="button"
                                                onClick={() => handleAddCurrency(product.id)}
                                                className="flex items-center gap-2 text-[#165FE3] text-[14px] font-semibold hover:text-blue-700"
                                            >
                                                <Plus className="w-4 h-4" />
                                                Add Currency
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex flex-row gap-4">
                        <button
                            type="button"
                            className="w-[100px] sm:w-full text-[14px] text-blue-500 p-3 bg-transparent border border-blue-500 rounded-lg flex items-center justify-center gap-2"
                            onClick={() => {
                                setCurrentStep((prev) => prev - 1)
                                dispatch(setForexProducts(
                                    products.map((product) => { 
                                        return {...product, currencies: [], checked: false}
                                    })
                                ));
                            }}
                        >
                            <ChevronLeft className="h-5 w-5" />
                            Back
                        </button>
                        <button
                            type="button"
                            className={`w-[100px] sm:w-full text-[14px] text-white-A700 p-3 rounded-lg flex items-center justify-center gap-2 ${
                                isNextDisabled
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-blue-700 border border-blue-500 hover:bg-blue-800"
                            }`}
                            onClick={handleNext}
                            disabled={isNextDisabled}
                        >
                            Next
                            <ChevronRight className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </form>
            <ToastContainer/>
        </div>
    );
};
